import './styles.css';
import { api } from './api';
import { prepareAttachment, type PreparedAttachment } from './attachments';
import { ApiError, type Attachment, type Diary, type DiaryInput, type Draft, type Mood, type StorageItem } from './types';
import { deriveTitle, insertMarkdownAttachment, markdownPreview, pendingAttachmentToken, removeMarkdownAttachment, renderMarkdown, replaceMarkdownAttachment } from './markdown';
import { filterDiaries, sortDiaries, type DiaryFilter, type DiarySort } from './state';

const APP_ORIGIN = 'https://diary.sb6657.uk';
const THEME_KEY = 'shiguang_theme';
const OFFLINE_DRAFT_KEY = 'shiguang_offline_draft_v2';
const THEME_MODE_KEY = 'shiguang_theme_mode';
const ACCENT_KEY = 'shiguang_accent';
const moods: Mood[] = [['平静', '🌿'], ['开心', '☀️'], ['感动', '💧'], ['疲惫', '🌙'], ['低落', '🌧️'], ['期待', '✨']].map(([name, icon]) => ({ name, icon }));

const get = <T extends Element>(selector: string) => document.querySelector<T>(selector)!;
const els = {
  app: get<HTMLElement>('#app'),
  entries: get<HTMLDivElement>('#entries'), content: get<HTMLElement>('#content'), modal: get<HTMLDivElement>('#editorModal'),
  body: get<HTMLTextAreaElement>('#bodyInput'), created: get<HTMLDivElement>('#createdLabel'),
  moods: get<HTMLDivElement>('#moods'), attachmentList: get<HTMLDivElement>('#attachmentEditorList'), attachments: get<HTMLInputElement>('#attachmentInput'),
  hint: get<HTMLSpanElement>('#saveHint'), toast: get<HTMLDivElement>('#toast'), status: get<HTMLDivElement>('#cloudStatus'),
  done: get<HTMLButtonElement>('#doneBtn'),
  settings: get<HTMLDivElement>('#settingsModal'), customAccent: get<HTMLInputElement>('#customAccent'),
  storage: get<HTMLDivElement>('#storageModal'), storageList: get<HTMLDivElement>('#storageList'), storageSummary: get<HTMLParagraphElement>('#storageSummary'),
  markdownPreview: get<HTMLDivElement>('#markdownPreviewModal'), markdownPreviewBody: get<HTMLDivElement>('#markdownPreviewBody'), markdownPreviewName: get<HTMLParagraphElement>('#markdownPreviewName'),
};

type EditorState = {
  diary: Diary | Draft;
  isNew: boolean;
  pending: Array<PreparedAttachment & { previewUrl: string | null; token: string }>;
  removedAttachmentIds: string[];
};

let diaries: Diary[] = [];
let cloudDraft: Draft | null = null;
let selectedId: string | null = null;
let filter: DiaryFilter = 'all';
let sortMode: DiarySort = 'date-desc';
let search = '';
let editing: EditorState | null = null;
let toastTimer: number | undefined;
let mobileDetailOpen = false;
let editorDirty = false;
let editorSaving = false;
let mobilePopBusy = false;
const mobileLayout = matchMedia('(max-width: 800px)');

type MobileView = 'exit' | 'list' | 'detail' | 'editor' | 'settings' | 'storage' | 'markdown' | 'lightbox';
type MobileHistory = { view: MobileView; diaryId: string | null };

function mobileHistory(state: unknown = history.state): MobileHistory | null {
  if (!state || typeof state !== 'object' || !('shiguangView' in state)) return null;
  const value = state as { shiguangView?: unknown; shiguangDiaryId?: unknown };
  const views: MobileView[] = ['exit', 'list', 'detail', 'editor', 'settings', 'storage', 'markdown', 'lightbox'];
  if (typeof value.shiguangView !== 'string' || !views.includes(value.shiguangView as MobileView)) return null;
  return { view: value.shiguangView as MobileView, diaryId: typeof value.shiguangDiaryId === 'string' ? value.shiguangDiaryId : null };
}

function mobileState(view: MobileView, diaryId: string | null = selectedId): Record<string, unknown> {
  return { shiguangView: view, ...(diaryId ? { shiguangDiaryId: diaryId } : {}) };
}

function initializeMobileHistory(): void {
  if (!mobileLayout.matches) return;
  const current = mobileHistory();
  if (current?.view === 'list') return;
  if (current?.view === 'exit') { history.pushState(mobileState('list', null), '', location.href); return; }
  if (current) { history.replaceState(mobileState('list', null), '', location.href); return; }
  history.replaceState(mobileState('exit', null), '', location.href);
  history.pushState(mobileState('list', null), '', location.href);
}

function syncMobileView(): void {
  els.app.classList.toggle('mobile-detail', mobileLayout.matches && mobileDetailOpen && Boolean(selectedId));
}

function enterMobileDetail(replaceCurrent = false): void {
  if (!mobileLayout.matches || !selectedId) { mobileDetailOpen = false; return; }
  mobileDetailOpen = true;
  const current = mobileHistory();
  if (current?.view === 'detail' && current.diaryId === selectedId) return;
  const method = replaceCurrent ? 'replaceState' : 'pushState';
  history[method](mobileState('detail'), '', location.href);
}

function showMobileList(): void {
  if (mobileLayout.matches && mobileHistory()?.view === 'detail') { history.back(); return; }
  mobileDetailOpen = false;
  syncMobileView();
  scrollTo({ top: 0, behavior: 'auto' });
}

function pushMobileOverlay(view: 'editor' | 'settings' | 'storage' | 'markdown' | 'lightbox'): void {
  if (!mobileLayout.matches || mobileHistory()?.view === view) return;
  history.pushState(mobileState(view), '', location.href);
}

function fmt(iso: string, detail = false): string {
  return new Intl.DateTimeFormat('zh-CN', detail ? { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long', hour: '2-digit', minute: '2-digit' } : { month: 'long', day: 'numeric', weekday: 'short' }).format(new Date(iso));
}

function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${(bytes / 1000).toFixed(bytes < 10_000 ? 1 : 0)} KB`;
  if (bytes < 1_000_000_000) return `${(bytes / 1_000_000).toFixed(bytes < 10_000_000 ? 1 : 0)} MB`;
  return `${(bytes / 1_000_000_000).toFixed(2)} GB`;
}

function toast(message: string): void {
  els.toast.textContent = message;
  els.toast.classList.add('show');
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => els.toast.classList.remove('show'), 2600);
}

function setStatus(kind: 'loading' | 'saving' | 'synced' | 'offline' | 'error', message: string): void {
  els.status.className = `cloud-status ${kind}`;
  els.status.lastElementChild!.textContent = message;
}

function button(className: string, label: string, title?: string): HTMLButtonElement {
  const element = document.createElement('button');
  element.className = className;
  element.textContent = label;
  if (title) { element.title = title; element.setAttribute('aria-label', title); }
  return element;
}

function renderList(): void {
  const list = sortDiaries(filterDiaries(diaries, search, filter), sortMode);
  els.entries.replaceChildren();
  if (!list.length) {
    const empty = document.createElement('div');
    empty.className = 'list-empty';
    empty.textContent = search || filter !== 'all' ? '没有找到日记' : '还没有写下第一篇日记';
    empty.style.cssText = 'padding:28px 12px;text-align:center;color:var(--muted);font-size:13px';
    els.entries.append(empty);
  }
  for (const diary of list) {
    const item = button(`entry ${diary.id === selectedId ? 'active' : ''}`, '');
    item.dataset.id = diary.id;
    const copy = document.createElement('div'); copy.className = 'entry-copy';
    const top = document.createElement('div'); top.className = 'entry-top';
    const date = document.createElement('span'); date.className = 'entry-date'; date.textContent = fmt(diary.createdAt);
    const marks = document.createElement('span'); marks.textContent = `${diary.starred ? '★ ' : ''}${diary.mood?.icon ?? ''}`;
    top.append(date, marks);
    const title = document.createElement('div'); title.className = 'entry-title'; title.textContent = diary.title || '无题日记';
    const preview = document.createElement('div'); preview.className = 'entry-preview'; preview.textContent = markdownPreview(diary.body) || '这一天没有留下文字';
    const size = document.createElement('div'); size.className = 'entry-size'; size.textContent = diary.storageBytes ? `附件 ${formatBytes(diary.storageBytes)}` : '无附件';
    copy.append(top, title, preview, size); item.append(copy);
    const firstImage = diary.attachments.find((attachment) => attachment.available && attachment.kind === 'image');
    if (firstImage) {
      const thumbnail = document.createElement('img'); thumbnail.className = 'entry-thumb'; thumbnail.src = firstImage.url; thumbnail.alt = ''; thumbnail.loading = 'lazy'; item.classList.add('has-thumb'); item.append(thumbnail);
    }
    els.entries.append(item);
  }
  get('#entryCount').textContent = `共 ${diaries.length} 篇`;
}

function renderContent(): void {
  const diary = diaries.find((item) => item.id === selectedId);
  els.content.replaceChildren();
  if (!diary) {
    const empty = document.createElement('div'); empty.className = 'empty';
    const box = document.createElement('div');
    const orbit = document.createElement('div'); orbit.className = 'empty-orbit'; orbit.textContent = '✎';
    const heading = document.createElement('h2'); heading.textContent = '写下今天';
    const copy = document.createElement('p'); copy.append('那些看似普通的瞬间，', document.createElement('br'), '会在未来成为珍贵的回忆。');
    const create = button('new-btn', '＋ 开始第一篇日记'); create.style.cssText = 'width:auto;margin:auto'; create.onclick = newDiary;
    box.append(orbit, heading, copy, create); empty.append(box); els.content.append(empty); return;
  }
  const article = document.createElement('article'); article.className = 'reader';
  const header = document.createElement('header'); header.className = 'reader-head';
  const back = button('mobile-back', '← 返回日记列表'); back.onclick = showMobileList;
  const info = document.createElement('div');
  const eyebrow = document.createElement('p'); eyebrow.className = 'eyebrow'; eyebrow.textContent = fmt(diary.createdAt);
  const meta = document.createElement('div'); meta.className = 'meta'; meta.textContent = `创建于 ${fmt(diary.createdAt, true)}${diary.updatedAt !== diary.createdAt ? ' · 后来编辑过' : ''}`;
  info.append(eyebrow, meta);
  const actions = document.createElement('div'); actions.className = 'head-actions';
  const star = button('icon-btn', diary.starred ? '★' : '☆', '收藏');
  star.onclick = async () => {
    try { setStatus('saving', '正在同步收藏…'); const result = await api.update(diary.id, { starred: !diary.starred, version: diary.version }); replaceDiary(result.diary); setStatus('synced', '已同步到云端'); }
    catch (error) { handleMutationError(error); }
  };
  const edit = button('icon-btn', '✎', '编辑'); edit.onclick = () => openEditor(diary, false);
  const remove = button('icon-btn', '⌫', '删除'); remove.onclick = async () => {
    if (!confirm('确定删除这篇日记吗？它的全部照片和附件都会从云端一并删除，且无法恢复。')) return;
    try { const hadMobileHistory = mobileHistory()?.view === 'detail'; setStatus('saving', '正在从云端删除…'); await api.remove(diary.id); diaries = diaries.filter((item) => item.id !== diary.id); selectedId = diaries[0]?.id ?? null; mobileDetailOpen = false; render(); if (hadMobileHistory) history.back(); setStatus('synced', '已同步到云端'); toast('日记已删除'); }
    catch (error) { handleMutationError(error); }
  };
  actions.append(star, edit, remove); header.append(back, info, actions);
  const section = document.createElement('section'); section.className = 'article';
  if (diary.mood) {
    const mood = document.createElement('div'); mood.className = 'mood-line';
    const icon = document.createElement('span'); icon.className = 'mood-big'; icon.textContent = diary.mood.icon;
    const label = document.createElement('span'); label.textContent = `这一天，我感到${diary.mood.name}`;
    mood.append(icon, label); section.append(mood);
  }
  const body = document.createElement('div'); body.className = 'body-text markdown-body';
  body.append(renderMarkdown(diary.body, diary.attachments, openLightbox, openAttachment));
  section.append(body);
  const legacyImages = diary.attachments.filter((attachment) => attachment.kind === 'image' && attachment.available && !diary.body.includes(`](${attachment.url})`));
  if (legacyImages.length) {
    const gallery = document.createElement('div'); gallery.className = 'gallery';
    legacyImages.forEach((attachment, index) => {
      const image = document.createElement('img'); image.src = attachment.url; image.alt = `日记照片 ${index + 1}`; image.loading = 'lazy';
      image.onclick = () => openLightbox(attachment.url);
      gallery.append(image);
    }); section.append(gallery);
  }
  article.append(header, section); els.content.append(article);
}

function render(): void { renderList(); renderContent(); syncMobileView(); }

function renderMoods(): void {
  els.moods.replaceChildren();
  for (const mood of moods) {
    const item = button(`mood ${editing?.diary.mood?.name === mood.name ? 'selected' : ''}`, `${mood.icon} ${mood.name}`);
    item.onclick = () => { if (!editing) return; editing.diary.mood = editing.diary.mood?.name === mood.name ? null : mood; renderMoods(); markEditorLocal(); };
    els.moods.append(item);
  }
}

function renderEditorAttachments(): void {
  els.attachmentList.replaceChildren();
  if (!editing) return;
  const items = [
    ...editing.diary.attachments.map((attachment) => ({ attachment })),
    ...editing.pending.map((pending) => ({ pending })),
  ];
  items.forEach((item) => {
    const value = 'attachment' in item ? item.attachment : item.pending;
    const wrapper = document.createElement('div'); wrapper.className = 'attachment-editor-item';
    const visual = document.createElement('div'); visual.className = 'attachment-editor-visual';
    if (value.kind === 'image' && ('attachment' in item ? item.attachment.available : Boolean(item.pending.previewUrl))) {
      const image = document.createElement('img'); image.src = 'attachment' in item ? item.attachment.url : item.pending.previewUrl!; image.alt = ''; visual.append(image);
    } else {
      visual.textContent = value.kind === 'pdf' ? 'PDF' : value.kind === 'markdown' ? 'MD' : value.kind === 'text' ? 'TXT' : value.kind === 'archive' ? 'ZIP' : 'FILE';
    }
    const copy = document.createElement('div'); copy.className = 'attachment-editor-copy';
    const name = document.createElement('strong'); name.textContent = 'attachment' in item ? item.attachment.name : item.pending.originalName;
    const meta = document.createElement('span');
    const size = 'attachment' in item ? item.attachment.size || item.attachment.originalSize : item.pending.blob.size;
    const state = 'attachment' in item ? (!item.attachment.available ? ' · 已删除' : '') : (item.pending.kind === 'archive' ? ' · 将封装为 ZIP' : '');
    meta.textContent = `${formatBytes(size)}${state}`;
    copy.append(name, meta);
    const remove = button('attachment-remove', '×', '移除附件');
    remove.onclick = () => {
      if (!editing) return;
      if ('attachment' in item) {
        editing.removedAttachmentIds.push(item.attachment.id); editing.diary.attachments = editing.diary.attachments.filter((attachment) => attachment.id !== item.attachment.id);
        els.body.value = removeMarkdownAttachment(els.body.value, item.attachment.url);
      } else {
        if (item.pending.previewUrl) URL.revokeObjectURL(item.pending.previewUrl);
        editing.pending = editing.pending.filter((pending) => pending !== item.pending);
        els.body.value = removeMarkdownAttachment(els.body.value, item.pending.token);
      }
      renderEditorAttachments(); markEditorLocal();
    };
    wrapper.append(visual, copy, remove); els.attachmentList.append(wrapper);
  });
}

function diaryInput(diary: Diary | Draft): DiaryInput {
  return { id: diary.id, title: deriveTitle(els.body.value), body: els.body.value, mood: diary.mood, starred: diary.starred, createdAt: diary.createdAt, version: diary.version };
}

function openEditor(diary: Diary | Draft, isNew: boolean): void {
  editing = { diary: structuredClone(diary), isNew, pending: [], removedAttachmentIds: [] };
  let body = diary.body ?? '';
  if (!isNew && diary.title && deriveTitle(body) !== diary.title) body = `# ${diary.title}\n\n${body}`.trim();
  for (const attachment of diary.attachments) {
    if (body.includes(`](${attachment.url})`)) continue;
    const marker = attachment.kind === 'image' ? `![${attachment.name}](${attachment.url})` : `[📎 ${attachment.name}](${attachment.url})`;
    body += `${body ? '\n\n' : ''}${marker}`;
  }
  els.body.value = body;
  els.created.textContent = `记录时间 · ${fmt(diary.createdAt, true)}`;
  get('#editorHeading').textContent = isNew ? '写下此刻' : '编辑这段时光';
  els.hint.textContent = '';
  editorDirty = false;
  renderMoods(); renderEditorAttachments(); els.modal.classList.add('show'); document.body.style.overflow = 'hidden';
  pushMobileOverlay('editor');
  window.setTimeout(() => els.body.focus(), 80);
}

function closeEditor(): void {
  editing?.pending.forEach((item) => { if (item.previewUrl) URL.revokeObjectURL(item.previewUrl); });
  editing = null; editorDirty = false; els.modal.classList.remove('show'); document.body.style.overflow = '';
}

async function newDiary(): Promise<void> {
  const draft = cloudDraft;
  if (draft && confirm('发现一份未完成的草稿，要继续写吗？')) { openEditor(draft, true); return; }
  const now = new Date().toISOString();
  openEditor({ id: crypto.randomUUID(), title: '', body: '', mood: null, starred: false, status: 'draft', createdAt: now, updatedAt: now, version: 0, storageBytes: 0, attachments: [] }, true);
}

function updateEditingFromFields(): void {
  if (!editing) return;
  editing.diary.title = deriveTitle(els.body.value); editing.diary.body = els.body.value; editing.diary.updatedAt = new Date().toISOString();
}

function markEditorLocal(): void {
  updateEditingFromFields();
  editorDirty = true;
  els.hint.textContent = '';
  if (editing) {
    const offline = { ...editing.diary, body: els.body.value, title: deriveTitle(els.body.value), savedAt: new Date().toISOString() };
    try { localStorage.setItem(OFFLINE_DRAFT_KEY, JSON.stringify(offline)); } catch { /* 文字草稿超过浏览器配额时仍保留在当前页面 */ }
  }
}

async function leaveEditor(): Promise<boolean> {
  if (!editing) return true;
  if (editorSaving) { toast('正在保存，请稍候'); return false; }
  updateEditingFromFields();
  if (!editing.isNew) {
    const wasDirty = editorDirty;
    if (wasDirty && !confirm('这次修改还没有保存，确定放弃并返回吗？')) return false;
    if (wasDirty) localStorage.removeItem(OFFLINE_DRAFT_KEY);
    closeEditor();
    if (wasDirty) toast('本次修改已放弃');
    return true;
  }
  const hasText = Boolean(els.body.value.trim());
  const hasPendingAttachments = editing.pending.length > 0;
  if (!hasText && !hasPendingAttachments && !editing.diary.attachments.length) {
    if (cloudDraft?.id === editing.diary.id) { await api.deleteDraft().catch(() => undefined); cloudDraft = null; }
    localStorage.removeItem(OFFLINE_DRAFT_KEY); closeEditor(); return true;
  }
  if (hasPendingAttachments && !confirm('返回不会上传新选择的附件，只保留文字草稿。确定继续吗？')) return false;
  if (!hasText) { closeEditor(); toast('未保存空白日记和待上传附件'); return true; }
  if (!await saveDraftOnce()) return false;
  closeEditor(); toast('文字草稿已保存到云端'); return true;
}

async function saveDraftOnce(): Promise<boolean> {
  if (!editing) return false;
  updateEditingFromFields();
  const input = diaryInput(editing.diary);
  try {
    setStatus('saving', '正在保存云端草稿…');
    const result = await api.saveDraft(input); cloudDraft = { ...result.draft, attachments: editing.diary.attachments };
    editing.diary.version = result.draft.version; setStatus('synced', '草稿已同步'); return true;
  } catch (error) {
    handleMutationError(error); els.hint.textContent = '草稿保存失败，编辑器仍保持打开'; return false;
  }
}

async function completeEditor(): Promise<void> {
  if (!editing) return;
  updateEditingFromFields();
  if (!els.body.value.trim() && !editing.diary.attachments.length && !editing.pending.length) { toast('至少写一点内容或添加一个附件再保存吧'); return; }
  editorSaving = true; els.done.disabled = true; els.hint.textContent = '正在保存到云端…'; setStatus('saving', '正在保存到云端…');
  try {
    const originalBody = els.body.value.trim();
    const pendingTokens = editing.pending.map((item) => item.token);
    const initialBody = pendingTokens.reduce((body, token) => removeMarkdownAttachment(body, token), originalBody);
    const input = diaryInput(editing.diary); input.body = initialBody; input.title = deriveTitle(initialBody || originalBody);
    let saved = editing.isNew ? (await api.create(input)).diary : (await api.update(editing.diary.id, { ...input, version: editing.diary.version })).diary;
    editing.isNew = false; editing.diary = saved;

    for (const attachmentId of [...editing.removedAttachmentIds]) {
      await api.deleteAttachment(attachmentId);
      saved.attachments = saved.attachments.filter((item) => item.id !== attachmentId);
      saved.storageBytes = saved.attachments.reduce((sum, item) => sum + (item.available ? item.size : 0), 0);
      editing.removedAttachmentIds = editing.removedAttachmentIds.filter((id) => id !== attachmentId);
    }

    let finalBody = originalBody;
    const pending = [...editing.pending];
    for (const [index, item] of pending.entries()) {
      els.hint.textContent = `正在上传附件 ${index + 1}/${pending.length} · ${item.originalName}`;
      const attachment = (await api.uploadAttachment(saved.id, item)).attachment;
      finalBody = replaceMarkdownAttachment(finalBody, item.token, attachment.url);
      els.body.value = finalBody;
      editing.pending = editing.pending.filter((pending) => pending !== item);
      editing.diary.attachments.push(attachment);
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    }

    if (finalBody !== initialBody || saved.title !== deriveTitle(finalBody)) {
      saved = (await api.update(saved.id, { body: finalBody, title: deriveTitle(finalBody), version: saved.version })).diary;
      editing.diary = saved;
    }

    replaceDiary(saved, false); await api.deleteDraft().catch(() => undefined); cloudDraft = null; localStorage.removeItem(OFFLINE_DRAFT_KEY);
    selectedId = saved.id; closeEditor(); enterMobileDetail(mobileHistory()?.view === 'editor'); render(); setStatus('synced', '已同步到云端'); toast('日记已保存到云端');
  } catch (error) { handleMutationError(error); els.hint.textContent = '保存失败，内容仍保留在编辑器中'; }
  finally { editorSaving = false; els.done.disabled = false; }
}

function replaceDiary(diary: Diary, rerender = true): void {
  const index = diaries.findIndex((item) => item.id === diary.id);
  if (index >= 0) diaries[index] = diary; else diaries.push(diary);
  if (rerender) render();
}

function handleMutationError(error: unknown): void {
  if (error instanceof ApiError && error.status === 409) { toast('这篇日记已在另一台设备更新，请刷新后再编辑'); setStatus('error', '检测到编辑冲突'); return; }
  toast(error instanceof Error ? error.message : '云端操作失败'); setStatus(navigator.onLine ? 'error' : 'offline', navigator.onLine ? '同步失败，请稍后重试' : '当前离线');
}

async function loadCloud(): Promise<void> {
  if (location.hostname === 'my-private-page.pages.dev') { location.replace(APP_ORIGIN); return; }
  try {
    setStatus('loading', '正在连接云端…'); await api.session(); const result = await api.list(); diaries = result.diaries; cloudDraft = result.draft; selectedId = diaries[0]?.id ?? null; mobileDetailOpen = false; render(); setStatus('synced', '已同步到云端');
    const offline = readOfflineDraft();
    if (offline) {
      if (confirm('发现一份浏览器中尚未完成的文字草稿，要继续编辑吗？')) {
        const current = diaries.find((item) => item.id === offline.id);
        if (current) openEditor({ ...current, body: offline.body, title: offline.title, mood: offline.mood, starred: offline.starred }, false);
        else openEditor({ ...offline, status: 'draft', storageBytes: offline.storageBytes ?? 0, attachments: offline.attachments ?? [] } as Draft, true);
      } else localStorage.removeItem(OFFLINE_DRAFT_KEY);
    }
  } catch (error) {
    els.content.innerHTML = '<div class="empty"><div><div class="empty-orbit">!</div><h2>暂时无法打开日记</h2><p>请确认网络正常，并从受保护的正式地址访问。</p><button class="new-btn" id="retryBtn" style="width:auto;margin:auto">重新连接</button></div></div>';
    get<HTMLButtonElement>('#retryBtn').onclick = loadCloud; handleMutationError(error);
  }
}

function readOfflineDraft(): (Diary | Draft) | null {
  const raw = localStorage.getItem(OFFLINE_DRAFT_KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<Diary | Draft>;
    if (typeof value.id !== 'string' || typeof value.body !== 'string' || typeof value.createdAt !== 'string') return null;
    return value as Diary | Draft;
  } catch { return null; }
}

type ThemeMode = 'system' | 'light' | 'dark';

function setThemeMode(mode: ThemeMode, persist = true): void {
  const dark = mode === 'dark' || (mode === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.dataset.theme = dark ? 'dark' : '';
  get('#themeBtn').textContent = dark ? '☀' : '☾';
  document.querySelectorAll<HTMLElement>('[data-theme-mode]').forEach((item) => item.classList.toggle('selected', item.dataset.themeMode === mode));
  if (persist) { localStorage.setItem(THEME_MODE_KEY, mode); localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light'); }
}

function setAccent(value: string, persist = true): void {
  if (!/^#[0-9a-f]{6}$/i.test(value)) return;
  document.documentElement.style.setProperty('--accent', value);
  document.documentElement.style.setProperty('--accent-soft', `color-mix(in srgb, ${value} 20%, transparent)`);
  document.querySelectorAll<HTMLElement>('[data-accent]').forEach((item) => item.classList.toggle('selected', item.dataset.accent?.toLowerCase() === value.toLowerCase()));
  els.customAccent.value = value;
  if (persist) localStorage.setItem(ACCENT_KEY, value);
}

get<HTMLButtonElement>('#newBtn').onclick = newDiary;
get<HTMLButtonElement>('#cancelBtn').onclick = async () => {
  if (!await leaveEditor()) return;
  if (mobileLayout.matches && mobileHistory()?.view === 'editor') history.back();
};
els.done.onclick = completeEditor;
els.body.addEventListener('input', markEditorLocal);
els.attachments.onchange = async (event) => {
  if (!editing) return;
  const availableCount = editing.diary.attachments.filter((item) => item.available).length;
  const remaining = Math.max(0, 50 - availableCount - editing.pending.length);
  const selected = [...((event.target as HTMLInputElement).files ?? [])];
  const files = selected.slice(0, remaining);
  if (selected.length > remaining) toast(`每篇日记最多 50 个附件，已选取前 ${remaining} 个`);
  els.hint.textContent = '正在检查附件…';
  for (const file of files) {
    try {
      els.hint.textContent = `正在处理 ${file.name}…`;
      const prepared = await prepareAttachment(file); const token = pendingAttachmentToken(crypto.randomUUID());
      const previewUrl = prepared.kind === 'image' ? URL.createObjectURL(file) : null;
      editing.pending.push({ ...prepared, previewUrl, token });
      const insertion = insertMarkdownAttachment(els.body.value, els.body.selectionStart, els.body.selectionEnd, token, prepared.originalName, prepared.kind);
      els.body.value = insertion.text; els.body.setSelectionRange(insertion.caret, insertion.caret);
    }
    catch (error) { toast(error instanceof Error ? error.message : '有一个附件无法读取'); }
  }
  renderEditorAttachments(); markEditorLocal(); els.attachments.value = ''; els.body.focus();
};
els.entries.onclick = (event) => { const target = (event.target as Element).closest<HTMLElement>('[data-id]'); if (!target) return; selectedId = target.dataset.id ?? null; enterMobileDetail(); render(); if (mobileDetailOpen) scrollTo({ top: 0, behavior: 'auto' }); };
get<HTMLInputElement>('#searchInput').oninput = (event) => { search = (event.target as HTMLInputElement).value; renderList(); };
get<HTMLSelectElement>('#sortSelect').onchange = (event) => { sortMode = (event.target as HTMLSelectElement).value as DiarySort; renderList(); };
get('.filter-row').addEventListener('click', (event) => { const target = (event.target as Element).closest<HTMLButtonElement>('[data-filter]'); if (!target) return; filter = target.dataset.filter as DiaryFilter; document.querySelectorAll('.filter').forEach((item) => item.classList.toggle('active', item === target)); renderList(); });
get<HTMLButtonElement>('#themeBtn').onclick = () => setThemeMode(document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark');
function openSettings(): void { els.settings.classList.add('show'); pushMobileOverlay('settings'); }
function closeSettings(): void { if (mobileLayout.matches && mobileHistory()?.view === 'settings') history.back(); else els.settings.classList.remove('show'); }
get<HTMLButtonElement>('#settingsBtn').onclick = openSettings;
get<HTMLButtonElement>('#settingsClose').onclick = closeSettings;
els.settings.onclick = (event) => { if (event.target === els.settings) closeSettings(); };
get('#themeModeOptions').addEventListener('click', (event) => {
  const target = (event.target as Element).closest<HTMLButtonElement>('[data-theme-mode]');
  if (target) setThemeMode(target.dataset.themeMode as ThemeMode);
});
get('#accentOptions').addEventListener('click', (event) => {
  const target = (event.target as Element).closest<HTMLButtonElement>('[data-accent]');
  if (target?.dataset.accent) setAccent(target.dataset.accent);
});
els.customAccent.oninput = () => setAccent(els.customAccent.value);
async function openStorage(): Promise<void> {
  els.settings.classList.remove('show'); els.storage.classList.add('show'); els.storageList.replaceChildren();
  els.storageSummary.textContent = '正在核对 D1 与 R2…'; pushMobileOverlay('storage');
  await loadStorage();
}

async function loadStorage(): Promise<void> {
  try {
    const result = await api.storage();
    syncStorageItems(result.items);
    els.storageSummary.textContent = `R2 中有 ${result.objectCount} 个对象，共 ${formatBytes(result.totalBytes)}；下方同时列出已删除占位。`;
    renderStorageItems(result.items);
  } catch (error) {
    els.storageSummary.textContent = error instanceof Error ? error.message : '文件清单读取失败';
  }
}

function syncStorageItems(items: StorageItem[]): void {
  const byId = new Map(items.filter((item) => item.attachmentId).map((item) => [item.attachmentId!, item]));
  for (const diary of diaries) {
    for (const attachment of diary.attachments) {
      const item = byId.get(attachment.id);
      if (!item) continue;
      attachment.available = item.status === 'normal'; attachment.size = item.status === 'normal' ? item.size : 0;
    }
    diary.storageBytes = diary.attachments.reduce((sum, item) => sum + (item.available ? item.size : 0), 0);
  }
  render();
}

function renderStorageItems(items: StorageItem[]): void {
  els.storageList.replaceChildren();
  if (!items.length) {
    const empty = document.createElement('p'); empty.className = 'storage-empty'; empty.textContent = 'R2 中暂时没有文件。'; els.storageList.append(empty); return;
  }
  for (const item of items) {
    const row = document.createElement('div'); row.className = `storage-item ${item.status}`;
    const copy = document.createElement('div'); copy.className = 'storage-item-copy';
    const name = document.createElement('strong'); name.textContent = item.name;
    const detail = document.createElement('span');
    const status = item.status === 'normal' ? '正常' : item.status === 'missing' ? '已从 R2 删除' : '孤儿文件';
    detail.textContent = `${status} · ${formatBytes(item.size)}${item.diaryTitle ? ` · ${item.diaryTitle}` : ''}`;
    const key = document.createElement('code'); key.textContent = item.objectKey;
    copy.append(name, detail, key); row.append(copy);
    if (item.status !== 'missing') {
      const remove = button('storage-delete', '删除', '永久删除此文件');
      remove.onclick = async () => {
        const message = item.status === 'orphan'
          ? `确定永久删除孤儿文件“${item.name}”吗？`
          : `确定从 R2 永久删除“${item.name}”吗？日记原位置会显示“该文件已被删除”。`;
        if (!confirm(message)) return;
        remove.disabled = true;
        try {
          if (item.attachmentId) { await api.deleteStorageAttachment(item.attachmentId); markAttachmentMissing(item.attachmentId); }
          else await api.deleteOrphan(item.objectKey);
          render(); await loadStorage(); toast('云端文件已删除');
        } catch (error) { handleMutationError(error); remove.disabled = false; }
      };
      row.append(remove);
    }
    els.storageList.append(row);
  }
}

function markAttachmentMissing(id: string): void {
  for (const diary of diaries) {
    const attachment = diary.attachments.find((item) => item.id === id);
    if (!attachment) continue;
    attachment.available = false; attachment.size = 0;
    diary.storageBytes = diary.attachments.reduce((sum, item) => sum + (item.available ? item.size : 0), 0);
  }
}

function closeStorage(): void {
  if (mobileLayout.matches && mobileHistory()?.view === 'storage') history.back(); else els.storage.classList.remove('show');
}

async function openMarkdownPreview(attachment: Attachment): Promise<void> {
  els.markdownPreview.classList.add('show'); els.markdownPreviewName.textContent = attachment.name;
  els.markdownPreviewBody.replaceChildren();
  const loading = document.createElement('p'); loading.textContent = '正在读取 Markdown…'; els.markdownPreviewBody.append(loading);
  pushMobileOverlay('markdown');
  try {
    const source = await api.markdownAttachment(attachment.id);
    els.markdownPreviewBody.replaceChildren(renderMarkdown(source, [], openLightbox, openAttachment));
  } catch (error) {
    els.markdownPreviewBody.replaceChildren(); const message = document.createElement('p'); message.textContent = error instanceof Error ? error.message : 'Markdown 预览失败'; els.markdownPreviewBody.append(message);
  }
}

function closeMarkdownPreview(): void {
  if (mobileLayout.matches && mobileHistory()?.view === 'markdown') history.back(); else els.markdownPreview.classList.remove('show');
}

function openAttachment(attachment: Attachment): void {
  if (!attachment.available) { toast('该文件已被删除'); return; }
  if (attachment.kind === 'image') { openLightbox(attachment.url); return; }
  if (attachment.kind === 'markdown') { void openMarkdownPreview(attachment); return; }
  const link = document.createElement('a');
  link.href = attachment.kind === 'text' || attachment.kind === 'pdf' ? attachment.url : attachment.downloadUrl;
  link.target = '_blank'; link.rel = 'noopener';
  if (!['text', 'pdf'].includes(attachment.kind)) link.download = attachment.storedName;
  link.click();
}

get<HTMLButtonElement>('#storageBtn').onclick = openStorage;
get<HTMLButtonElement>('#settingsStorageBtn').onclick = openStorage;
get<HTMLButtonElement>('#storageClose').onclick = closeStorage;
els.storage.onclick = (event) => { if (event.target === els.storage) closeStorage(); };
get<HTMLButtonElement>('#markdownPreviewClose').onclick = closeMarkdownPreview;
els.markdownPreview.onclick = (event) => { if (event.target === els.markdownPreview) closeMarkdownPreview(); };
function openLightbox(url: string): void { get<HTMLImageElement>('#lightboxImg').src = url; get('#lightbox').classList.add('show'); pushMobileOverlay('lightbox'); }
function closeLightbox(): void { if (mobileLayout.matches && mobileHistory()?.view === 'lightbox') history.back(); else get('#lightbox').classList.remove('show'); }
get('#lightbox').addEventListener('click', closeLightbox);
els.modal.onclick = (event) => { if (event.target === els.modal) toast('请点“完成”保存，或点“取消”退出'); };
document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && get('#lightbox').classList.contains('show')) closeLightbox(); if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && els.modal.classList.contains('show')) completeEditor(); });
window.addEventListener('offline', () => setStatus('offline', '当前离线 · 编辑内容仅留在本页'));
window.addEventListener('online', () => { if (editing) setStatus('synced', '网络已恢复 · 尚未上传编辑内容'); else loadCloud(); });

const savedMode = (localStorage.getItem(THEME_MODE_KEY) ?? localStorage.getItem(THEME_KEY) ?? 'system') as ThemeMode;
setThemeMode(['system', 'light', 'dark'].includes(savedMode) ? savedMode : 'system', false);
setAccent(localStorage.getItem(ACCENT_KEY) ?? '#bd694b', false);
matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => { if ((localStorage.getItem(THEME_MODE_KEY) ?? 'system') === 'system') setThemeMode('system', false); });
mobileLayout.addEventListener('change', (event) => { if (event.matches) { mobileDetailOpen = false; initializeMobileHistory(); } syncMobileView(); scrollTo({ top: 0, behavior: 'auto' }); });
window.addEventListener('popstate', (event) => { void (async () => {
  if (!mobileLayout.matches || mobilePopBusy) return;
  const target = mobileHistory(event.state);
  if (editing && target?.view !== 'editor') {
    mobilePopBusy = true;
    const canLeave = await leaveEditor();
    mobilePopBusy = false;
    if (!canLeave) { history.forward(); return; }
  }
  els.settings.classList.toggle('show', target?.view === 'settings');
  els.storage.classList.toggle('show', target?.view === 'storage');
  els.markdownPreview.classList.toggle('show', target?.view === 'markdown');
  get('#lightbox').classList.toggle('show', target?.view === 'lightbox');
  if (target?.view === 'detail' || target?.view === 'lightbox') {
    const diaryId = target.diaryId;
    if (diaryId && diaries.some((diary) => diary.id === diaryId)) selectedId = diaryId;
    mobileDetailOpen = Boolean(selectedId); render(); return;
  }
  if (target?.view === 'editor') return;
  if (target?.view === 'exit') {
    if (confirm('确定要退出拾光吗？返回后可能需要重新登录。')) history.back();
    else history.forward();
    return;
  }
  mobileDetailOpen = false; syncMobileView(); scrollTo({ top: 0, behavior: 'auto' });
})(); });
initializeMobileHistory();
loadCloud();
